import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { agenciesAPI } from '../../services/api';
import Card from '../common/Card';
import StatsCard from '../dashboard/StatsCard';
import Loading from '../common/Loading';
import Button from '../common/Button';
import { handleApiError } from '../../utils/helpers';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState({
    totalAgencies: 0,
    activeAgencies: 0,
    inactiveAgencies: 0,
    freePlanAgencies: 0,
    paidPlanAgencies: 0,
  });
  const [recentAgencies, setRecentAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await agenciesAPI.getAll();
      const agencies = response.data.agencies || [];

      // Calculate stats
      setStats({
        totalAgencies: agencies.length,
        activeAgencies: agencies.filter((a) => a.is_active !== false).length,
        inactiveAgencies: agencies.filter((a) => a.is_active === false).length,
        freePlanAgencies: agencies.filter((a) => a.plan_price === 0 || !a.plan_price).length,
        paidPlanAgencies: agencies.filter((a) => a.plan_price > 0).length,
      });

      // Get recent agencies (last 5)
      setRecentAgencies(agencies.slice(0, 5));
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={fetchDashboardData} variant="secondary">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Super Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">Overview of all agencies and system statistics</p>
        </div>
        <Link to="/super-admin/agencies">
          <Button variant="primary">Manage Agencies</Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatsCard
          title="Total Agencies"
          value={stats.totalAgencies}
          color="blue"
          icon="🏢"
        />
        <StatsCard
          title="Active Agencies"
          value={stats.activeAgencies}
          color="green"
          icon="✅"
        />
        <StatsCard
          title="Inactive Agencies"
          value={stats.inactiveAgencies}
          color="red"
          icon="❌"
        />
        <StatsCard
          title="Free Plan"
          value={stats.freePlanAgencies}
          color="yellow"
          icon="🆓"
        />
        <StatsCard
          title="Paid Plans"
          value={stats.paidPlanAgencies}
          color="purple"
          icon="💳"
        />
      </div>

      {/* Recent Agencies */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Recent Agencies</h2>
          <Link to="/super-admin/agencies">
            <Button variant="secondary" size="sm">
              View All
            </Button>
          </Link>
        </div>

        {recentAgencies.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No agencies yet</p>
        ) : (
          <div className="space-y-4">
            {recentAgencies.map((agency) => (
              <Link
                key={agency.id}
                to={`/super-admin/agencies/${agency.id}`}
                className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{agency.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Admin: {agency.admin_name} ({agency.admin_email})
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Plan: {agency.plan_name || 'N/A'}
                    </p>
                  </div>
                  <div className="ml-4 text-right">
                    <span
                      className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        agency.is_active === false
                          ? 'bg-red-100 text-red-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {agency.is_active === false ? 'Inactive' : 'Active'}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default SuperAdminDashboard;








